import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { Prompt } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/pro-light-svg-icons';
import Loader from '../General/Loader';
import ConfirmPrompt from '../General/ConfirmPrompt';
import { confirmAlert } from 'react-confirm-alert';
import {
  getScopeWheelHeroes,
  createScopeWheelHero,
  updateScopeWheelHero,
  deleteScopeWheelHero,
  proposeScopeWheelHeroes
} from '../../api/scope_wheel_heroes';
import { TABS, todayContextLine, blankHero, payloadFromHero, filterHeroes } from '../../helpers/scope_wheel_helpers';
import WheelProposalCard from './WheelProposalCard';
import WheelHeroCard from './WheelHeroCard';
import WheelHeroEditor from './WheelHeroEditor';
import './Wheel.scss';

const NEW_ID = 'new';

const Wheel = () => {
  const [heroes, setHeroes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState('wheel');
  const [query, setQuery] = useState('');
  const [liveOnly, setLiveOnly] = useState(false);
  const [videosOnly, setVideosOnly] = useState(false);
  const [visibleCount, setVisibleCount] = useState(30);
  const [selectedId, setSelectedId] = useState(null);
  const [extraContext, setExtraContext] = useState('');
  const [isProposing, setIsProposing] = useState(false);
  const [editorState, setEditorState] = useState({ dirty: false, uploading: false, saving: false });
  const hasPendingChanges = editorState.dirty || editorState.uploading || editorState.saving;

  useEffect(() => {
    if (!hasPendingChanges) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasPendingChanges]);

  useEffect(() => {
    getScopeWheelHeroes()
      .then(setHeroes)
      .catch(err => window.ALERT.error(typeof err === 'string' ? err : 'Failed to load the wheel.'))
      .finally(() => setIsLoading(false));
  }, []);

  const matching = filterHeroes(heroes, { tab, query, liveOnly, videosOnly });
  const visible = matching.slice(0, visibleCount);
  useEffect(() => setVisibleCount(30), [tab, query, liveOnly, videosOnly]);
  const ideaCount = heroes.filter(hero => hero.status === 'proposed').length;

  const leaveEditor = action => {
    if (editorState.saving) return;
    const proceed = () => { setSelectedId(null); setEditorState({ dirty: false, uploading: false, saving: false }); action(); };
    if (!hasPendingChanges) return proceed();
    confirmAlert({ customUI: ({ onClose }) => <ConfirmPrompt
      hideInput header='Discard hero changes?'
      subheader={editorState.uploading ? 'The upload is still in progress. Leaving will discard this hero draft.' : 'Your hero edits have not been saved.'}
      cancelBtnDisplay='Keep editing' submitBtnDisplay='Discard changes'
      onCancel={onClose} onSubmit={() => { onClose(); proceed(); }} /> });
  };

  const upsertHero = saved => {
    setHeroes(prev => {
      const without = prev.filter(hero => hero.id !== saved.id);
      return saved.status === 'rejected' || saved.status === 'archived' ? [...without, saved] : [saved, ...without];
    });
    setSelectedId(saved.id);
  };

  const propose = async () => {
    if (isProposing) return;
    setIsProposing(true);
    setTab('ideas');
    try {
      const proposed = await proposeScopeWheelHeroes(extraContext);
      setHeroes(prev => [...proposed, ...prev]);
      window.ALERT.success(`Spun up ${proposed.length} idea${proposed.length === 1 ? '' : 's'}.`);
    } catch (err) {
      window.ALERT.error(typeof err === 'string' ? err : 'Could not spin up new ideas.');
    } finally {
      setIsProposing(false);
    }
  };

  const decide = async (hero, status, term) => {
    const previous = heroes;
    const next = { ...hero, term: term ?? hero.term, status, isLive: status === 'accepted' ? hero.isLive : false };
    setHeroes(prev => prev.map(item => (item.id === hero.id ? next : item)));
    try {
      const saved = await updateScopeWheelHero(hero.id, payloadFromHero(next));
      setHeroes(prev => prev.map(item => (item.id === hero.id ? saved : item)));
    } catch (err) {
      setHeroes(previous);
      window.ALERT.error(typeof err === 'string' ? err : 'Could not save that decision.');
    }
  };

  const saveHero = async draft => {
    try {
      const saved = draft.id
        ? await updateScopeWheelHero(draft.id, payloadFromHero(draft))
        : await createScopeWheelHero(payloadFromHero({ ...draft, status: 'accepted' }));
      upsertHero(saved);
      if (!draft.id) setTab('wheel');
      window.ALERT.success(saved.isLive ? 'Published.' : 'Saved as a draft.');
      return saved;
    } catch (err) {
      window.ALERT.error(typeof err === 'string' ? err : 'Failed to save that hero.');
    }
  };

  const removeHero = async hero => {
    const previous = heroes;
    setHeroes(prev => prev.filter(item => item.id !== hero.id));
    setSelectedId(prev => (prev === hero.id ? null : prev));
    try {
      await deleteScopeWheelHero(hero.id);
    } catch (err) {
      setHeroes(previous);
      window.ALERT.error(typeof err === 'string' ? err : 'Failed to remove that hero.');
    }
  };

  const confirmRemove = hero => confirmAlert({ customUI: ({ onClose }) => <ConfirmPrompt
    hideInput header={`Remove “${hero.term || 'this hero'}”?`} subheader='This cannot be undone.'
    submitBtnDisplay='Remove' onCancel={onClose} onSubmit={() => { onClose(); removeHero(hero); }} /> });

  const shelveHero = hero => leaveEditor(() => decide(hero, 'archived', hero.term));
  const restoreHero = hero => leaveEditor(() => {
    setTab('wheel');
    setQuery('');
    setLiveOnly(false);
    setVideosOnly(false);
    decide(hero, 'accepted');
  });

  if (isLoading) {
    return (
      <div className='wheel-admin-outer'>
        <Loader />
      </div>
    );
  }

  return (
    <div className='wheel-admin-outer'>
      <Prompt when={hasPendingChanges} message='You have unsaved hero changes or an upload in progress. Leave without saving?' />
      <div className='wheel-admin-header'>
        <div>
          <h1>
            <FontAwesomeIcon icon={faSync} className='wheel-admin-mark' />
            Wheel
          </h1>
          <div className='wheel-admin-subtitle'>Hero videos and sparks for the home wheel. {todayContextLine()}</div>
        </div>
        <div className='wheel-admin-actions'>
          <button type='button' className='wheel-admin-btn wheel-admin-btn--ghost' onClick={() => leaveEditor(() => { setTab('wheel'); setSelectedId(NEW_ID); setQuery(''); setLiveOnly(false); setVideosOnly(false); })} disabled={selectedId === NEW_ID}>
            Add a hero
          </button>
          <button type='button' className={cn('wheel-admin-btn wheel-admin-btn--primary', { spinning: isProposing })} onClick={() => leaveEditor(propose)} disabled={isProposing}>
            <FontAwesomeIcon icon={faSync} />
            {isProposing ? 'Spinning…' : 'Propose new ones'}
          </button>
        </div>
      </div>

      <div className='wheel-admin-context'>
        <input
          value={extraContext}
          onChange={event => setExtraContext(event.target.value)}
          placeholder="What's going on right now? NYFW, a holiday, a vibe…"
          onKeyDown={event => event.key === 'Enter' && leaveEditor(propose)}
        />
      </div>

      <div className='wheel-admin-tabs'>
        {TABS.map(item => (
          <button
            key={item.id}
            type='button'
            className={cn('wheel-admin-tab', { active: tab === item.id })}
            onClick={() => leaveEditor(() => setTab(item.id))}
          >
            {item.label}
            {item.id === 'ideas' && ideaCount > 0 && <span className='wheel-admin-count'>{ideaCount}</span>}
          </button>
        ))}
      </div>

      <div className='wheel-admin-filters'>
        <input aria-label='Search hero library' placeholder='Search titles, themes or product links' value={query} onChange={event => { const value = event.target.value; leaveEditor(() => setQuery(value)); }} />
        <label><input type='checkbox' checked={videosOnly} onChange={event => { const value = event.target.checked; leaveEditor(() => setVideosOnly(value)); }} />Videos only</label>
        <label><input type='checkbox' checked={liveOnly} onChange={event => { const value = event.target.checked; leaveEditor(() => setLiveOnly(value)); }} />Live only</label>
        <span>{matching.length} matching · {heroes.filter(hero => hero.targetKind === 'shopmy' && hero.isLive && hero.videoUrl).length} live ShopMy videos</span>
      </div>

      {tab === 'ideas' && !visible.length && (
        <div className='wheel-admin-empty'>
          Nothing cooking. Hit Propose new ones and we will riff on today
          {extraContext ? ` — and on “${extraContext.trim()}”` : ''}.
        </div>
      )}

      {tab === 'ideas' && (
        <div className='wheel-admin-ideas'>
          {visible.map(hero => (
            <WheelProposalCard key={hero.id} hero={hero} onYes={(term) => decide(hero, 'accepted', term)} onNo={() => decide(hero, 'rejected')} />
          ))}
        </div>
      )}

      {tab !== 'ideas' && (
        <div className='wheel-admin-list'>
          {selectedId === NEW_ID && tab === 'wheel' && (
            <div className='wheel-admin-new'>
              <WheelHeroEditor hero={blankHero()} onSave={saveHero} onCancel={() => leaveEditor(() => {})} onStateChange={setEditorState} />
            </div>
          )}
          {!visible.length && selectedId !== NEW_ID && (
            <div className='wheel-admin-empty'>
              {tab === 'wheel' ? 'No matching heroes — upload a video or change the filters.' : 'Nothing shelved.'}
            </div>
          )}
          {visible.map(hero => (
            <WheelHeroCard
              key={hero.id}
              hero={hero}
              isSelected={selectedId === hero.id}
              onSelect={() => leaveEditor(() => setSelectedId(selectedId === hero.id ? null : hero.id))}
            >
              {selectedId === hero.id && (
                <WheelHeroEditor
                  hero={hero}
                  onSave={saveHero}
                  onCancel={() => leaveEditor(() => {})}
                  onStateChange={setEditorState}
                  onShelve={hero.status === 'accepted' ? () => shelveHero(hero) : undefined}
                  onRestore={hero.status !== 'accepted' ? () => restoreHero(hero) : undefined}
                  onRemove={() => leaveEditor(() => confirmRemove(hero))}
                />
              )}
            </WheelHeroCard>
          ))}
        </div>
      )}
      {visible.length < matching.length && <button type='button' className='wheel-admin-btn wheel-admin-btn--ghost' onClick={() => setVisibleCount(count => count + 30)}>Show more ({matching.length - visible.length} remaining)</button>}
    </div>
  );
};

export default Wheel;
